import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useInFlightDeliveryOrderQuery } from './DeliveryInFlight.generated';

import type { RequestPolicy } from 'urql';

/**
 * If there is a delivery order currently in flight, we prevent the customer from placing a new delivery order.
 * This is the case when the terminal_status in gravy is not set for any of the last three orders.
 * The issue is that this is not updated in an event-based manner, but rather only when we query order status.
 * Thus we must re-query the order status if there is an in-flight delivery order to update it.
 * @see {@link https://sweetgreen.atlassian.net/browse/CELS-5271?focusedCommentId=228081}
 */
export const useDeliveryOrderInFlight = (props?: UseDeliveryOrderInFlightProps) => {
  const { pause = false, requestPolicy = 'cache-and-network' } = props ?? {};
  const isLoggedIn = useIsLoggedIn();

  const [response] = useInFlightDeliveryOrderQuery({
    requestPolicy,
    pause: pause || !isLoggedIn,
  });

  const orderId = response.data?.inFlightDeliveryOrder?.id;

  const router = useRouter();

  const navigateToDeliveryOrderInFlightScreen = useCallback(() => {
    if (!orderId) return;

    router.navigate({ pathname: '/delivery-order-in-flight', params: { orderId } });
  }, [orderId, router]);

  return { inFlightDeliveryOrderId: orderId, navigateToDeliveryOrderInFlightScreen };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseDeliveryOrderInFlightProps = Readonly<{
  requestPolicy?: RequestPolicy;
  pause?: boolean;
}>;
